"use client";

import { useEffect } from "react";

/**
 * Mounts global window.onerror + unhandledrejection handlers and
 * forwards anything they catch to /api/errors. Renders nothing.
 *
 * Mounted once in the root layout's <Providers>. Safe to mount on
 * every page — handlers are added on mount and removed on unmount,
 * and the POSTs are fire-and-forget.
 *
 * Why this matters: without it, client-side crashes (broken builds,
 * bad data, race conditions) are invisible to the server. The user
 * sees a white screen and we have no idea what happened.
 */
export function ErrorTracker() {
  useEffect(() => {
    let lastReportedAt = 0;
    let reportsThisSecond = 0;

    // Crude in-memory rate limit so a runaway error loop doesn't DDoS
    // our own /api/errors endpoint. The server has its own per-IP limit
    // but we should be polite to ourselves first.
    const shouldReport = () => {
      const now = Date.now();
      if (now - lastReportedAt > 1000) {
        lastReportedAt = now;
        reportsThisSecond = 1;
        return true;
      }
      reportsThisSecond++;
      return reportsThisSecond <= 5; // max 5/sec
    };

    const report = (payload: Record<string, unknown>) => {
      if (!shouldReport()) return;
      try {
        fetch("/api/errors", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            url: window.location.href,
            userAgent: navigator.userAgent,
          }),
          // Use keepalive so the request still goes out if the user is
          // navigating away (which is exactly when crashes often happen).
          keepalive: true,
        }).catch(() => {
          /* Swallow — never let the reporter itself break the page. */
        });
      } catch {
        /* swallow */
      }
    };

    const onError = (ev: ErrorEvent) => {
      // Filter out cross-origin script errors that we can't action on.
      // These show up as message="Script error." with no other detail.
      if (ev.message === "Script error." && !ev.error) return;

      report({
        message: ev.message || "Unknown error",
        stack: ev.error?.stack,
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
      });
    };

    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const stack =
        reason instanceof Error ? reason.stack : reason?.stack;
      // Drop only the empty case: no reason, no stack. Safari rejects
      // aborted fetches + cancelled native APIs with bare `undefined`,
      // which lands in the log as "Unhandled promise rejection: undefined"
      // with null stack — zero diagnostic value. Anything with a stack
      // OR a real value (Error, string, object) still flows through.
      if ((reason === undefined || reason === null) && !stack) return;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : reason?.message || String(reason);
      // Benign, non-actionable media teardown races. The camera scanner
      // (html5-qrcode on /admin/intake + /admin/consignments) calls
      // video.play() internally; when the scanner unmounts mid-start the
      // browser rejects the in-flight play() with "The play() request was
      // interrupted because the media was removed from the document" (or
      // "...by a call to pause()"). Nothing to fix in our code — drop it.
      if (message.includes("play() request was interrupted")) return;
      report({
        message: `Unhandled promise rejection: ${message}`,
        stack,
        context: { kind: "unhandledrejection" },
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
